import { useMutation } from '@apollo/client';
import _, { find, findIndex, isEmpty } from 'lodash';
import React, { ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    ContactInput,
    FieldInput,
    LocationInput,
    ProductionInput,
    ProductionOrganizationInfoInput,
    ReferenceInput
} from 'sr-types/lib/production/v1/graphql';
import { UserContext } from '../../common/auth/UserContext';
import { useStepper } from '../../common/components/StepperContext';
import { BUTTON_VARIANTS, FILTER_IDENTIFIERS } from '../../common/constants/constants';
import { FormProvider } from '../../common/form/FormContext';
import FormModal from '../../common/form/FormModal';
import Validator from '../../common/form/Validator';
import { useL10n } from '../../common/hooks/useL10n';
import I18n from '../../common/i18n/I18n';
import { PLANNING } from '../../common/nav/apps';
import { createReferenceFromIdAndName, failedToastMessage, successToastMessage } from '../../common/utils/commonUtils';
import useHistory from '../../common/utils/useHistory';
import CreateProductionInStepper from '../../hiringManager/CreateProductionInStepper';
import { ProductionModes } from '../ProductionList';
import { extractNames } from '../helpers/peopleUtils';
import { client, createEmptyProduction, saveQuery, toAssociatedOrgs, toAssociatedPeople } from '../helpers/production';
import {
    modifyPersonToContactInput,
    productionListeners,
    useLazyProductionDetailsData
} from '../helpers/productionApi';
import {
    EMPTY_PERSON_MODAL_STATE,
    EMPTY_PRODUCTION_DATES,
    FILTER_FIELD_NAMES,
    getDefaultOwnerContact,
    ProductionWithReference,
    ValidationRulesType
} from '../helpers/productionUtils';
import { PRODUCTION_MODE, STATE_TYPE } from '../productionConstants';
import ProductionFormModal from './CreateProductionFormModal';
import { doSavePerson } from './production/AddProjectOwnerModal';

type ProductionCreateFormProps = {
    title?: string | ReactElement;
    clone?: boolean;
    productionId?: string;
    isOpen?: boolean;
    onCancel?: () => void;
    onSave?: (productionId: string, values: ProductionWithReference) => void;
    enableConvertingToManaged?: boolean;
    isInModal?: boolean;
    focusField?: string;
    state?: (typeof STATE_TYPE)[keyof typeof STATE_TYPE];
};

export default function CreateProductionForm(props: ProductionCreateFormProps) {
    const {
        title,
        clone,
        productionId: prodIdProps,
        isOpen = true,
        onCancel,
        onSave,
        enableConvertingToManaged = false,
        isInModal = true,
        focusField,
        state
    } = props;
    const { activeOrganizationAccount, application, userProfile } = useContext(UserContext);
    const { searchParams } = useHistory();
    const { locale, l10n } = useL10n();

    // Access stepper context (will be null if not in stepper mode)
    const stepperContext = useStepper(false);

    const defaultOwnerContact = useMemo(() => getDefaultOwnerContact(userProfile), [userProfile]);
    const PRODUCTION_EMPTY_STATE = useMemo(() => {
        return {
            ...createEmptyProduction(locale),
            projectOwnerContacts: defaultOwnerContact ? [defaultOwnerContact] : [],
            name: undefined
        };
    }, [defaultOwnerContact, locale]);
    const productionMode = searchParams.get(PRODUCTION_MODE);
    const guestEditorRef = useRef(null);
    const isPersonalProfile = !userProfile?.currentOwnerOrganizationIds?.length;

    const [productionId, setProductionId] = useState(prodIdProps);

    const { getProductionDetailsData, productionDetailsData, productionDetailsLoading } =
        useLazyProductionDetailsData(productionId);

    const [tempProd, setTempProd] = useState<ReferenceInput>({ id: '', label: '' });
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

    // Initialize state from stepper storage if in stepper mode, otherwise use empty state
    const [values, setValues] = useState<ProductionWithReference>(() => {
        if (!isInModal && stepperContext) {
            const stepperData = stepperContext.getStepData(stepperContext.activeStep);
            if (stepperData) {
                return stepperData;
            }
        }
        return PRODUCTION_EMPTY_STATE;
    });

    const [isSaveDisabled, setIsSaveDisabled] = useState(false);
    const [isFormSaving, setIsFormSaving] = useState(false);
    const [personModalState, setPersonModalState] = useState(EMPTY_PERSON_MODAL_STATE);
    const [initialProductionState, setInitialProductionState] = useState<ProductionWithReference>(null);
    const [hasHydratedProductionValues, setHasHydratedProductionValues] = useState<boolean>(() => !prodIdProps);
    const hasLoadedProductionDetails = Boolean(productionDetailsData?.production?.identity?.id);
    const shouldRenderModalFormBody =
        !productionId || (hasHydratedProductionValues && hasLoadedProductionDetails && !productionDetailsLoading);

    useEffect(() => {
        if (productionId) {
            setHasHydratedProductionValues(false);
            getProductionDetailsData();
        }
    }, [getProductionDetailsData, productionId]);

    const [save, { loading: isSaving }] = useMutation(saveQuery, {
        client: client,
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        }
    });

    const validationRules: ValidationRulesType = useMemo(() => {
        const otherTitles = find(values?.['otherTitles'], { key: 'AKA' });
        return personModalState.open
            ? {}
            : {
                  [enableConvertingToManaged ? 'name' : 'name.label']:
                      otherTitles && otherTitles.value != ''
                          ? []
                          : [
                                {
                                    isValid: (prop, state, isModified, resolveI18nToken) => {
                                        const value = _.get(state, prop);

                                        const valid = enableConvertingToManaged ? !!value?.label : !!value;
                                        return valid ? true : resolveI18nToken('validation.value.required');
                                    },
                                    isRequired: true
                                }
                            ],
                  otherTitles: values?.name?.label
                      ? []
                      : [
                            {
                                isValid: (prop, state, isModified, resolveI18nToken) => {
                                    const value = _.get(state, prop);
                                    const otherTitles = find(value, { key: 'AKA' });
                                    const valid = !!otherTitles?.value;
                                    return valid ? true : resolveI18nToken('validation.value.required');
                                },
                                isRequired: true
                            }
                        ],
                  ownerOrganization: isPersonalProfile ? [] : [Validator.RULES.isRequired]
              };
    }, [values, personModalState.open, enableConvertingToManaged, isPersonalProfile]);

    useEffect(() => {
        if (values?.name?.id && values?.name.label) {
            if (!values.name.managedProduction) {
                setIsDialogOpen(true);
                setTempProd({ id: values.name.id, label: values.name.label });
            } else if (values.name.managedProduction) setProductionId(values.name.id);
        }
    }, [values?.name, values?.name?.id]);

    useEffect(() => {
        // Guard: Only run when we have production details to load
        // AND the form should be populated (either in modal mode OR a production was selected)
        // Skip if:
        // - No production details loaded yet, OR
        // - User is clearing the field (values.name is undefined/empty)
        if (!productionDetailsData?.production?.identity?.id) {
            return;
        }

        // When creating a new production, if user clears the name field after selecting one,
        // don't repopulate the form. Only populate if:
        // - We're in modal mode (editing existing), OR
        // - A production with ID is currently selected in the name field
        if (!isInModal && !values?.name?.id) {
            return;
        }

        if (productionDetailsData?.production?.identity?.id && (isInModal || values?.name?.id)) {
            let associatedOrganizations = [];
            productionDetailsData.production.associatedOrgs?.map((orgs) => {
                associatedOrganizations.push({
                    organizationReference: orgs.organizationReference,
                    organization: orgs.name,
                    productionRoles: orgs.productionRoles,
                    organizationTypes: orgs.organizationTypes,
                    organizationCategories: orgs.organizationCategories
                });
            });
            const {
                otherTitles,
                productionType,
                productionSubType,
                about,
                budget,
                productionGroup,
                genre,
                productionStatus,
                productionYears,
                name,
                currentStatusNoteReference,
                attributes,
                associatedFieldDefinitionReferences,
                projectOwnerContacts,
                tags
            } = productionDetailsData.production;

            const newProd = {
                ...productionDetailsData.production,
                name: { id: '', label: name },
                type: productionType || '',
                productionSubType: productionSubType || '',
                summary: {
                    about
                },
                budget: {
                    currencyCode: budget && budget.currencyCode ? budget.currencyCode : l10n.defaultCurrency,
                    nanos: budget && budget.nanos,
                    units: budget && budget.units ? budget.units : 0
                },
                otherTitles: otherTitles,
                productionGroup: productionGroup?.productionReference
                    ? { ...productionGroup.productionReference, label: productionGroup?.label }
                    : null,
                genres: genre || [],
                productionStatus: productionStatus,
                productionYears: productionYears,
                alternateIds: productionDetailsData.production.alternateIds || [],
                associatedOrgs: associatedOrganizations,
                guests: productionDetailsData.production.linkedPeople?.map((person) => person.contact) || [],
                productionDates: productionDetailsData?.production?.productionDates?.dateRange?.start
                    ? productionDetailsData.production.productionDates
                    : { ...EMPTY_PRODUCTION_DATES.productionDates },
                ownerOrganization: createReferenceFromIdAndName(
                    productionDetailsData.production?.ownerOrganizationId || '',
                    productionDetailsData.production?.ownerOrganizationName || ''
                ),
                attributes,
                associatedFieldDefinitionReferences,
                ...(currentStatusNoteReference ? { currentStatusNoteReference } : {}),
                projectOwnerContacts:
                    Array.isArray(projectOwnerContacts) && projectOwnerContacts.length > 0
                        ? projectOwnerContacts
                        : defaultOwnerContact
                          ? [defaultOwnerContact]
                          : [],
                tags: tags || []
            };
            // TODO What needs to be reset if we're cloning another production?
            if (clone) {
                newProd.name = '';
                newProd.about = '';
                const akaIndex = findIndex(newProd.otherTitles, { key: 'AKA' });
                if (akaIndex >= 0) {
                    newProd.otherTitles[akaIndex].value = '';
                }
            }
            setValues((prev) => ({ ...prev, ...newProd }));
            setInitialProductionState(newProd);
            setHasHydratedProductionValues(true);
        }
    }, [
        isInModal,
        values?.name?.id,
        productionDetailsData?.production,
        l10n.defaultCurrency,
        defaultOwnerContact,
        clone
    ]);

    const onCreateOwner = (e) => {
        setPersonModalState({
            open: true,
            prepopulate: { name: extractNames(e.label), email: '' }
        });
    };

    const onCreateOwnerCanceled = () => {
        setValues((prev) => ({
            ...prev,
            projectOwnerContacts: prev.projectOwnerContacts?.filter(
                (owner: ContactInput & { createOption: boolean }) => !owner.createOption
            )
        }));
        setPersonModalState(EMPTY_PERSON_MODAL_STATE);
    };

    const doSave = () => {
        if (personModalState.open)
            doSavePerson(
                setIsFormSaving,
                (input) => {
                    setValues((prev) => {
                        const filteredContacts = prev.projectOwnerContacts?.filter(
                            (owner: ContactInput & { createOption: boolean }) => !owner.createOption
                        );
                        return { ...prev, projectOwnerContacts: [...(filteredContacts || []), input] };
                    });
                },
                guestEditorRef,
                setPersonModalState
            );
        else {
            const name = values.name;
            const productionGroup = values.productionGroup
                ? {
                      label: values.productionGroup.label,
                      productionReference: values.productionGroup
                  }
                : {};
            const savedEntity: ProductionInput = {
                publicTitle: name.label,
                summary: {
                    headline: values.headline || '',
                    about: values.about || ''
                },
                budget: {
                    currencyCode: values.budget.currencyCode || l10n.defaultCurrency,
                    nanos: values.budget.nanos || undefined,
                    units: values.budget.units || undefined
                },
                productionType: values.productionType || '',
                productionGroup,
                projectStatus: values.projectStatus || '',
                projectOwnerContacts: modifyPersonToContactInput(values.projectOwnerContacts),
                ownerOrganizationId: values?.ownerOrganization?.id || '',
                attributes: values?.attributes as FieldInput[],
                associatedFieldDefinitionReferences: values?.associatedFieldDefinitionReferences,
                tags: values?.tags,
                ...(values.productionSubType ? { productionSubType: values.productionSubType } : {})
            };

            if (productionId && !clone) {
                savedEntity.identity = productionDetailsData?.production?.identity;
                savedEntity.managedProduction = values.managedProduction;
            }
            if (
                (application.home === PLANNING.home && productionMode === ProductionModes.workspace) ||
                enableConvertingToManaged
            ) {
                savedEntity.managedProduction = true;
            }
            if (values.releaseDate) {
                savedEntity.releaseDate = values.releaseDate;
            }
            if (values.productionYears) {
                savedEntity.productionYears = values.productionYears;
            }
            if (values.otherTitles && values.otherTitles.length) {
                savedEntity['otherTitles'] = values.otherTitles.filter((item) => item.value);
            }
            if (values?.genre?.length) {
                savedEntity.genre = values.genre;
            }
            if (typeof values.productionStatus === 'string' && values.productionStatus) {
                savedEntity.productionStatus = values.productionStatus;
            }
            if (values.locations && values.locations.length) {
                const locs = values.locations.map((location) => {
                    return { ...location, primary: location.primary || false };
                });
                savedEntity.locations = locs as LocationInput[];
            }
            if (!isEmpty(values.alternateIds)) {
                savedEntity.alternateIds = values.alternateIds;
            }
            if (values.associatedOrgs && values.associatedOrgs.length) {
                savedEntity.associatedOrgs = toAssociatedOrgs(values.associatedOrgs);
            }
            if (values.linkedPeople && values.linkedPeople.length) {
                savedEntity.linkedPeople = toAssociatedPeople(values.linkedPeople);
            }
            if (values?.productionDates?.dateRange.start) {
                savedEntity.productionDates = values.productionDates;
            }
            if (values?.keyPeople?.length) {
                savedEntity.keyPeople = values.keyPeople as ContactInput[];
            }
            if (values?.keyOrganizations?.length) {
                savedEntity.keyOrganizations = values.keyOrganizations as ProductionOrganizationInfoInput[];
            }
            if (values.currentStatusNoteReference?.id) {
                savedEntity.currentStatusNoteReference = values.currentStatusNoteReference;
            }
            //we have to clear empty attribute values because we have validation on the backend for that.
            if (Array.isArray(values?.showAttributes) && values.showAttributes.length > 0) {
                savedEntity.showAttributes = values.showAttributes.filter((attr) => attr?.key && attr?.value);
            }
            if (state) {
                savedEntity.state = state;
            }

            const handleUpdateProductionInList = (updatedProduction) => {
                if (productionId && !tempProd.label && onSave) {
                    onSave(productionId, updatedProduction);
                }
            };

            handleUpdateProductionInList({ ...values, name: values.name.label, id: productionId });

            save({
                variables: {
                    input: savedEntity
                }
            })
                .then((res) => {
                    if (res.data.saveProduction.errors) {
                        failedToastMessage('form.save.failure', { name: name.label });
                        handleUpdateProductionInList(initialProductionState);
                    } else {
                        successToastMessage('form.save.success', { name: name.label });
                        if (!productionId || tempProd.label) {
                            onSave?.(res.data.saveProduction.id, values);
                        }
                    }
                })
                .catch((err) => {
                    failedToastMessage('form.save.failure', { name: name.label });
                    handleUpdateProductionInList(initialProductionState);
                });
        }
    };

    return (
        <FormProvider
            validationRules={validationRules}
            state={values}
            setState={setValues}
            expandedSections={['production-basic-info']}
            listeners={productionListeners(locale)}
        >
            {isInModal ? (
                <FormModal
                    id={'create-production-form'}
                    title={title}
                    isOpen={isOpen}
                    isLoading={productionDetailsLoading}
                    isSaving={isSaving || isFormSaving}
                    isSaveDisabled={isSaving || isSaveDisabled}
                    expanded
                    onSave={doSave}
                    onClose={onCancel}
                    additionalActions={
                        personModalState.open
                            ? [
                                  {
                                      label: <I18n token="form.previous" />,
                                      onClick: onCreateOwnerCanceled,
                                      variant: BUTTON_VARIANTS.contained,
                                      disabled: isFormSaving
                                  }
                              ]
                            : []
                    }
                >
                    {shouldRenderModalFormBody && (
                        <ProductionFormModal
                            personModalState={personModalState}
                            setIsSaveDisabled={setIsSaveDisabled}
                            guestEditorRef={guestEditorRef}
                            enableConvertingToManaged={enableConvertingToManaged}
                            onCreateOwner={onCreateOwner}
                            isDialogOpen={isDialogOpen}
                            setIsDialogOpen={setIsDialogOpen}
                            setProductionId={setProductionId}
                            tempProd={tempProd}
                            productionFilter={{
                                expressions: [
                                    { field: FILTER_IDENTIFIERS.managedProduction, value: { values: ['false'] } }
                                ]
                            }}
                            focusField={focusField}
                        />
                    )}
                </FormModal>
            ) : (
                <CreateProductionInStepper
                    personModalState={personModalState}
                    setIsSaveDisabled={setIsSaveDisabled}
                    guestEditorRef={guestEditorRef}
                    enableConvertingToManaged={enableConvertingToManaged}
                    onCreateOwner={onCreateOwner}
                    isDialogOpen={isDialogOpen}
                    setIsDialogOpen={setIsDialogOpen}
                    setProductionId={setProductionId}
                    tempProd={tempProd}
                    productionFilter={{
                        expressions: [
                            { field: FILTER_IDENTIFIERS.managedProduction, value: { values: ['true'] } },
                            { field: FILTER_FIELD_NAMES.state, value: { values: [STATE_TYPE.managed] } }
                        ]
                    }}
                />
            )}
        </FormProvider>
    );
}
